import { isAdminOrganization } from '@rematter/common/identity';
import { adminModule } from '@rematter/modules/admin';
import { customerPortalModule } from '@rematter/modules/customer-portal';
import { useAtomValue } from 'jotai';
import { Redirect, Route, Switch } from 'wouter';

import { RightSidebarProvider } from './contexts/RightSidebarContext';
import { AppLayout } from './layouts/AppLayout';
import { AcceptInvitation } from './routes/accept-invitation';
import { CustomerInvitationAccept } from './routes/customer-invitation-accept';
import { SignIn } from './routes/sign-in';
import { SignUp } from './routes/sign-up';
import { VerifyEmail } from './routes/verify-email';

import { isPublicPath } from '~/config/routes';
import { SharedChatRoute } from '~/modules/assistant/_components/SharedChatRoute';
import { auditLogModule } from '~/modules/audit-log';
import { automationsModule } from '~/modules/automations';
import { brokerageModule } from '~/modules/brokerage';
import { calendarModule } from '~/modules/calendar';
import { crmModule } from '~/modules/crm';
import { facilitiesModule } from '~/modules/facilities';
import { filesModule } from '~/modules/files';
import { financeModule } from '~/modules/finance';
import { inventoryModule } from '~/modules/inventory';
import { officeModule } from '~/modules/office';
import { pricingModule } from '~/modules/pricing';
import { productionModule } from '~/modules/production';
import { reportsModule } from '~/modules/reports';
import { settingsModule } from '~/modules/settings';
import { tradeModule } from '~/modules/trade';
import { transportationModule } from '~/modules/transportation';
import { useIdentity } from '~/shared/hooks';
import { useTranslations } from '~/shared/i18n/useTranslations';
import { useEntitlements } from '~/shared/providers/auth';
import { createSignInRedirectPath } from '~/shared/routing/authRedirect';
import { ModuleDefinition } from '~/shared/routing/types';
import {
  $allSettingsGroups,
  $defaultLandingModule,
  $settingsReadPermission,
  $userSidebarModuleOrder,
} from '~/shared/state/settings.state';
import { sortModulesByOrder } from '~/shared/utils/sortNavItems';

const appModules: ModuleDefinition[] = [
  // auditLogModule,
  automationsModule,
  brokerageModule,
  calendarModule,
  pricingModule,
  crmModule,
  facilitiesModule,
  filesModule,
  financeModule,
  inventoryModule,
  officeModule,
  productionModule,
  reportsModule,
  tradeModule,
  transportationModule,
  // settingsModule,
  customerPortalModule,
];

const adminModules: ModuleDefinition[] = [adminModule];

const modules = [...appModules, ...adminModules];

// NOTE: Ordering of this array matters. It will be exactly the order the modules are displayed in
// the sidebar.
export const appSidebarModules: ModuleDefinition[] = [
  customerPortalModule,
  reportsModule,
  tradeModule,
  crmModule,
  inventoryModule,
  productionModule,
  pricingModule,
  brokerageModule,
  financeModule,
  transportationModule,
  calendarModule,
  filesModule,
  facilitiesModule,
  automationsModule,
];

export const adminSidebarModules: ModuleDefinition[] = [adminModule];

export const availableModules = modules;

export const filterModulesByEntitlements = (
  modules: ModuleDefinition[],
  entitlements: string[],
): ModuleDefinition[] => {
  const hasExclusiveEntitlement = modules.some(
    (module) =>
      module.isExclusive &&
      module.requiredEntitlement &&
      entitlements.includes(module.requiredEntitlement),
  );

  return modules.filter((module) => {
    if (!module.requiredEntitlement) {
      return !hasExclusiveEntitlement;
    }
    return entitlements.includes(module.requiredEntitlement);
  });
};

type DefaultRouteProps = {
  availableModules: ModuleDefinition[];
  sidebarModules: ModuleDefinition[];
};

const DefaultRoute = ({
  availableModules,
  sidebarModules,
}: DefaultRouteProps) => {
  const defaultLandingModule = useAtomValue($defaultLandingModule);
  const moduleOrder = useAtomValue($userSidebarModuleOrder);
  const permissionCheck = useAtomValue($settingsReadPermission);
  const { isPending, isFetching } = useAtomValue($allSettingsGroups);
  const { entitlements } = useEntitlements();

  if (permissionCheck.isPending && permissionCheck.isFetching) return null;
  if (isPending && isFetching) return null;

  if (defaultLandingModule) {
    const isAvailable = availableModules.some(
      (m) =>
        defaultLandingModule === m.path ||
        defaultLandingModule.startsWith(m.path + '/'),
    );
    if (isAvailable) {
      return <Redirect to={defaultLandingModule} replace />;
    }
  }

  const filteredSidebar = filterModulesByEntitlements(
    sidebarModules,
    entitlements,
  );
  const sorted = sortModulesByOrder(filteredSidebar, moduleOrder);
  const firstModule = sorted[0];

  if (firstModule) {
    return <Redirect to={firstModule.path} replace />;
  }

  return null;
};

export const AppRouter = () => {
  const t = useTranslations();
  const { isLoading, isLoggedIn, organization } = useIdentity();
  const { entitlements } = useEntitlements();

  if (isLoading) {
    return null;
  }

  // Change location explicitly to reset app state
  const currentPath = window.location.pathname;
  if (!isLoggedIn && !isPublicPath(currentPath)) {
    window.location.href = createSignInRedirectPath();
    return null;
  }

  const isAdminOrg = isAdminOrganization(organization);
  const filteredAppModules = filterModulesByEntitlements(
    appModules,
    entitlements,
  );

  return (
    <Switch>
      {/* Public Routes */}
      <Route path="/sign-in">
        <SignIn />
      </Route>
      <Route path="/sign-up">
        <SignUp />
      </Route>
      <Route path="/verify-email">
        <VerifyEmail />
      </Route>
      <Route path="/accept-invitation">
        <AcceptInvitation />
      </Route>
      <Route path="/customer/accept-invitation">
        <CustomerInvitationAccept />
      </Route>

      {/* Protected Routes - Wrapped with AppLayout */}
      <Route>
        <RightSidebarProvider>
          <AppLayout>
            <settingsModule.Router />
            <auditLogModule.Router />
            <Switch>
              {/* Shared chat route */}
              <Route path="/assistant/share/:token">
                <SharedChatRoute />
              </Route>

              {/* App module routes */}
              {!isAdminOrg &&
                filteredAppModules.map(({ path, Router }) => (
                  <Route key={path} path={path}>
                    <Router />
                  </Route>
                ))}

              {/* App module routes with wildcards */}
              {!isAdminOrg &&
                filteredAppModules.map(({ path, Router }) => (
                  <Route key={`${path}/*`} path={`${path}/*`}>
                    <Router />
                  </Route>
                ))}

              {/* Admin module routes */}
              {isAdminOrg &&
                adminModules.map(({ path, Router }) => (
                  <Route key={path} path={path}>
                    <Router />
                  </Route>
                ))}

              {/* Admin module routes with wildcards */}
              {isAdminOrg &&
                adminModules.map(({ path, Router }) => (
                  <Route key={`${path}/*`} path={`${path}/*`}>
                    <Router />
                  </Route>
                ))}

              {/* Default route */}
              <Route path="/">
                <DefaultRoute
                  availableModules={
                    isAdminOrg ? adminModules : filteredAppModules
                  }
                  sidebarModules={
                    isAdminOrg ? adminSidebarModules : appSidebarModules
                  }
                />
              </Route>

              {/* 404 fallback */}
              <Route>
                <div className="p-4">{t.pageNotFound}</div>
              </Route>
            </Switch>
          </AppLayout>
        </RightSidebarProvider>
      </Route>
    </Switch>
  );
};

export default AppRouter;
